import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useBuildStatus } from '../hooks/useBuildStatus';
import { useUserProfile } from '../hooks/useUserProfile';
import { PRODUCT_NAME } from '../brand';
import UsageMeter from './UsageMeter';

export default function NavBar() {
  const { user, devMode, logout } = useAuth();
  const location = useLocation();
  const { isLatest, latestBuild, minutesSince, error, loading } = useBuildStatus();
  const { profile } = useUserProfile();
  const isAdmin = profile?.role === 'admin' || devMode;

  // Ops access comes from /auth/me (`ops`). The old probe hit
  // /ops_admin/orgs from every customer page load and each 403 landed in
  // the audit trail as auth.failure with no user (AW plan D4 nit (b)).
  const isOpsUser = profile?.ops === true;

  // Render build status badge
  const renderBuildBadge = () => {
    const version = latestBuild?.package_version || import.meta.env.VITE_APP_VERSION || '0.0.0';
    const buildId = (latestBuild?.build_id || import.meta.env.VITE_BUILD_ID || '').slice(0, 8);
    const versionStr = buildId && buildId !== 'unknown' ? `v${version} #${buildId}` : `v${version}`;
    const label = `${PRODUCT_NAME} ${versionStr}`;
    
    if (loading) {
      return <span className="build-badge loading">{label}...</span>;
    }
    
    if (error) {
      return <span className="build-badge">{label}</span>;
    }

    if (isLatest === true) {
      const timeago = minutesSince !== null && minutesSince > 0 
        ? ` (${minutesSince}m ago)` 
        : '';
      return (
        <span className="build-badge latest" title={`Latest build${timeago}`}>
          ✓ {label}
        </span>
      );
    } else if (isLatest === false) {
      return (
        <span className="build-badge outdated" title="Newer build available - click to refresh" onClick={() => window.location.reload()}>
          ⟳ {label}
        </span>
      );
    }

    return <span className="build-badge">{label}</span>;
  };

  return (
    <nav className="navbar">
      <div className="navbar-brand">
        <Link to="/cases" className="navbar-logo">
          <img src="/chargestack-logo.png" alt="Chargestack" className="navbar-logo-img" />
        </Link>
        {renderBuildBadge()}
      </div>

      {/* The app's main areas, beside the logo. Sentencing Research is its own
          area, not part of a case, and shows to the people it is enabled for
          (features.sentencing_tools). Account and admin links stay on the right. */}
      {user && (
        <div className="navbar-tabs" role="navigation" aria-label="Main">
          <Link
            to="/cases"
            className={`navbar-tab${location.pathname === '/cases' || location.pathname.startsWith('/case/') ? ' active' : ''}`}
          >
            Cases
          </Link>
          {profile?.features?.sentencing_tools && (
            <Link to="/sentencing" className={`navbar-tab${location.pathname === '/sentencing' ? ' active' : ''}`}>
              Sentencing Research
            </Link>
          )}
        </div>
      )}

      {devMode && <div className="dev-banner">DEV MODE</div>}

      {user && (
        <div className="navbar-right">
          <UsageMeter scope={isAdmin ? 'org' : 'me'} variant="compact" />
          {isAdmin && (
            <Link to="/settings" className={`btn btn-sm btn-ghost${location.pathname === '/settings' ? ' active' : ''}`}>
              Settings
            </Link>
          )}
          {isOpsUser && (
            <Link to="/ops_admin" className={`btn btn-sm btn-ghost ops-admin-nav-link${location.pathname.startsWith('/ops_admin') ? ' active' : ''}`}>
              Ops Admin
            </Link>
          )}
          <span className="navbar-user">{user.name || user.email}</span>
          <button className="btn btn-sm btn-ghost" onClick={logout}>
            Sign out
          </button>
        </div>
      )}
    </nav>
  );
}
